// Carga y escritura de las banderitas de las tablas (spec 2026-10-22, § 7.2 y § 7.3).
import type { ApolloCache } from '@apollo/client';
import type { AuthContextType } from '../../hooks/useAuth';
import { isOfflineQueuedError } from '../../offline/isOfflineQueuedError';
import { isEffectivelyOnline } from '../../offline/connectivity';
import {
  orderFlagChangeFailed,
  orderFlagChangeRequested,
  orderFlagChangeSettled,
  orderFlagsLoaded,
  orderFlagsReset,
  orderFlagsScopeChecked,
} from '../../state/actions/orderFlags';
import type {
  OrderFlagEntry,
  OrderFlagScope,
  OrderFlagStateEntry,
} from '../../state/reducers/orderFlags';
import {
  selectConfirmedFlaggedOrderIds,
  selectOrderFlagScope,
  selectOrderFlagScopeCheckedAt,
  selectOrderFlagsLoadedAt,
  selectOrderFlagsOwnerKey,
} from '../../state/selectors/orderFlags';
import { GET_MY_ORDER_FLAGS, GET_MY_ORDER_FLAG_SCOPE } from '../queries/orderFlags';
import { SET_ORDER_FLAGS } from '../mutations/orderFlags';

/** Una carga más reciente que esto (mismo dueño) no se repite. */
export const ORDER_FLAGS_STALE_MS = 60_000;

/** El ámbito (sello) se comprueba como mucho una vez cada tanto. */
export const ORDER_FLAG_SCOPE_CHECK_MS = 10_000;

/**
 * Tope de lo que un listado de «Solo marcadas» espera a las marcas en vuelo
 * (`orderFlagsBarrierLink`): con la red atascada, mejor la lista sin la última
 * marca que una búsqueda colgada.
 */
export const ORDER_FLAG_WRITES_WAIT_CAP_MS = 3_000;

// ── Marcas en vuelo ────────────────────────────────────────────────────────
// QA en beta (2026-10-23): marcar y pulsar Buscar con «Solo importantes» en
// ~100 ms mandaba el listado antes de que el servidor guardara la marca, y
// salía 1 de 2 hasta la búsqueda siguiente. Cada `setOrderFlags` se apunta
// aquí AL EMPEZAR (antes de cualquier `await`, así cuenta aunque la mutation
// aún no haya salido) y se borra al terminar —éxito, error o encolada sin
// red—, y la barrera de Apollo retiene los listados con `onlyFlagged` hasta
// que acaban las que ya estaban en vuelo.
const pendingOrderFlagWrites = new Set<Promise<void>>();

/** ¿Hay alguna marca o desmarca sin terminar? */
export function hasPendingOrderFlagWrites(): boolean {
  return pendingOrderFlagWrites.size > 0;
}

/**
 * Espera a que terminen las marcas que YA estaban en vuelo al llamar (las que
 * empiecen después no alargan la espera), como mucho `timeoutMs`. Nunca
 * rechaza; sin marcas en vuelo resuelve al momento.
 */
export function waitForPendingOrderFlagWrites(
  timeoutMs: number = ORDER_FLAG_WRITES_WAIT_CAP_MS
): Promise<void> {
  const inFlight = Array.from(pendingOrderFlagWrites);
  if (inFlight.length === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    void Promise.allSettled(inFlight).then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

function trackOrderFlagWrite(write: Promise<void>): Promise<void> {
  pendingOrderFlagWrites.add(write);
  const settle = () => {
    pendingOrderFlagWrites.delete(write);
  };
  write.then(settle, settle);
  return write;
}

type FlagsStore = {
  dispatch: (action: any) => unknown;
  getState: () => any;
};

type FlagsClient = {
  query: (options: any) => Promise<any>;
  mutate: (options: any) => Promise<any>;
  /** La del cliente real; un cliente de pega (tests) puede no traerla. */
  cache?: ApolloCache<unknown>;
};

export type OrderFlagsDeps = {
  store: FlagsStore;
  client: FlagsClient;
  isOnline: () => boolean;
  now: () => number;
  notifyError: (msg: string) => void;
  /**
   * Tira de la caché de Apollo los listados de «Solo marcadas». Se llama cada
   * vez que cambia el conjunto de marcas (decisión 11): sin ello, Buscar sobre
   * la entrada cacheada fusiona y la orden desmarcada no se va nunca.
   */
  evictOnlyFlagged: () => void;
};

/**
 * Los valores reales se resuelven al usarse (import dinámico): así importar
 * este módulo no arrastra Apollo, redux-persist ni react-toastify al cargarlo.
 */
async function resolveDeps(
  overrides: Partial<OrderFlagsDeps> = {}
): Promise<OrderFlagsDeps> {
  const store =
    overrides.store ?? (await import('../../state/configureStore')).store;
  const client = overrides.client ?? (await import('../client')).client;
  const evictOnlyFlagged =
    overrides.evictOnlyFlagged ?? (await defaultEvictOnlyFlagged(client));
  const notifyError =
    overrides.notifyError ??
    ((msg: string) => {
      void import('../../utils/toasts').then((m) => m.showErrorToast(msg));
    });
  return {
    store,
    client,
    // El detector EFECTIVO (red + backend que contesta), el mismo que usa
    // `OfflineQueueLink`: con solo `isOnlineNow` una tablet en un hotspot sin
    // datos pasaba la puerta y pintaba una foto vieja (revisión I1).
    isOnline: overrides.isOnline ?? isEffectivelyOnline,
    now: overrides.now ?? Date.now,
    notifyError,
    evictOnlyFlagged,
  };
}

/** La limpieza real, sobre la caché de Apollo del cliente (si la tiene). */
async function defaultEvictOnlyFlagged(
  client: FlagsClient
): Promise<() => void> {
  const { evictOnlyFlaggedListings } = await import('../cacheHelpers');
  return () => {
    if (client.cache) evictOnlyFlaggedListings(client.cache);
  };
}

/** Limpieza best-effort: un fallo de la caché no frena la marca. */
function safeEvictOnlyFlagged(d: OrderFlagsDeps): void {
  try {
    d.evictOnlyFlagged();
  } catch {
    // Sin limpieza, lo peor es la fila de más hasta la siguiente carga.
  }
}

/** ¿Mismo conjunto de órdenes? (el orden no cuenta). */
function sameOrderIds(a: Iterable<string>, b: Iterable<string>): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const id of left) if (!right.has(id)) return false;
  return true;
}

/** `${userId}:${tenantId}`; `null` si falta alguno de los dos. */
export function buildOrderFlagsOwnerKey(
  userData: AuthContextType | null
): string | null {
  const userId = userData?.user?.user?.id;
  const tenantId =
    userData?.activeTenantId || userData?.user?.user?.activeTenantId;
  if (!userId || !tenantId) return null;
  return `${userId}:${tenantId}`;
}

/** Carga las banderitas del usuario. Nunca lanza. */
export async function refreshOrderFlags(
  userData: AuthContextType | null,
  deps?: Partial<OrderFlagsDeps>
): Promise<void> {
  try {
    const ownerKey = buildOrderFlagsOwnerKey(userData);
    if (!ownerKey) return;
    const d = await resolveDeps(deps);

    // Otro dueño: se vacía antes de pedir, para no enseñar marcas ajenas.
    const state = d.store.getState();
    const sameOwner = selectOrderFlagsOwnerKey(state) === ownerKey;
    if (!sameOwner) d.store.dispatch(orderFlagsReset(ownerKey));

    if (!d.isOnline()) return;

    // Ámbito comprobado hace poco: varios loaders seguidos no lo repiten.
    // `?? null` en los selectores: el estado persistido puede ser anterior.
    const scopeCheckedAt = sameOwner ? selectOrderFlagScopeCheckedAt(state) : null;
    if (
      scopeCheckedAt != null &&
      d.now() - scopeCheckedAt < ORDER_FLAG_SCOPE_CHECK_MS
    ) {
      return;
    }

    const loadedAt = sameOwner ? selectOrderFlagsLoadedAt(state) : null;

    // Pide el ámbito (pequeño). Si falla —back viejo sin `myOrderFlagScope`,
    // o red mala— se vuelve a la regla de siempre: lista entera cada 60 s.
    // Mismo `no-cache` que abajo, por la misma razón.
    let scope: OrderFlagScope | null = null;
    try {
      const scopeResult = await d.client.query({
        query: GET_MY_ORDER_FLAG_SCOPE,
        fetchPolicy: 'no-cache',
      });
      scope = scopeResult?.data?.myOrderFlagScope ?? null;
    } catch {
      scope = null;
    }

    if (scope) {
      const known = sameOwner ? selectOrderFlagScope(d.store.getState()) : null;
      // Mismo sello y ya hubo una carga: nada cambió, no se baja la lista.
      if (known && known.stamp === scope.stamp && loadedAt != null) {
        d.store.dispatch(orderFlagsScopeChecked(ownerKey, scope, d.now()));
        return;
      }
    } else if (loadedAt != null && d.now() - loadedAt < ORDER_FLAGS_STALE_MS) {
      return;
    }

    // `no-cache` y no `network-only`: sin backend, `client.tsx` activa
    // `prioritizeCacheValues` y Apollo convierte `network-only` en
    // `cache-first`, así que devolvería la última carga guardada como si fuera
    // nueva. Así además no se escriben hasta 2.000 `OrderFlag` en la caché
    // persistida (las marcas viven en Redux, no en Apollo).
    // Hora de la PETICIÓN: lo que se confirme mientras vuela no lo pisa la
    // respuesta (carrera carga ↔ toque).
    const requestedAt = d.now();
    const result = await d.client.query({
      query: GET_MY_ORDER_FLAGS,
      fetchPolicy: 'no-cache',
    });
    const entries: OrderFlagEntry[] = result?.data?.myOrderFlags ?? [];
    // Lo confirmado JUSTO antes de aplicar la foto (tras un reset, vacío).
    const previous = selectConfirmedFlaggedOrderIds(d.store.getState());
    d.store.dispatch(
      orderFlagsLoaded(ownerKey, entries, d.now(), requestedAt)
    );
    // El servidor dice otra cosa (otro equipo marcó o desmarcó): las listas
    // de «Solo marcadas» cacheadas ya no valen (decisión 11).
    if (!sameOrderIds(previous, entries.map((e) => e.orderId))) {
      safeEvictOnlyFlagged(d);
    }
    // El ámbito se guarda DESPUÉS de la lista: si la carga falla, el sello
    // viejo se queda y el siguiente refresco lo reintenta.
    if (scope) d.store.dispatch(orderFlagsScopeChecked(ownerKey, scope, d.now()));
  } catch {
    // Sin marcas es mejor que una pantalla rota: se conserva el estado.
  }
}

/**
 * Marca/desmarca de forma optimista. Nunca lanza. Mientras dura cuenta como
 * marca en vuelo (ver `waitForPendingOrderFlagWrites`).
 */
export function setOrderFlags(
  orderIds: string[],
  flagged: boolean,
  deps?: Partial<OrderFlagsDeps>
): Promise<void> {
  if (orderIds.length === 0) return Promise.resolve();
  return trackOrderFlagWrite(writeOrderFlags(orderIds, flagged, deps));
}

async function writeOrderFlags(
  orderIds: string[],
  flagged: boolean,
  deps?: Partial<OrderFlagsDeps>
): Promise<void> {
  let d: OrderFlagsDeps;
  try {
    d = await resolveDeps(deps);
  } catch {
    return;
  }
  const changedAt = new Date(d.now()).toISOString();
  d.store.dispatch(orderFlagChangeRequested(orderIds, flagged, changedAt));
  // En el toque, sin esperar a la mutation: la siguiente búsqueda/refresco con
  // «Solo marcadas» entra limpia (REPLACE_ALL). La fila sigue a la vista hasta
  // entonces (P6). Si la mutation falla, el servidor no cambió y la limpieza
  // solo cuesta una petición.
  safeEvictOnlyFlagged(d);
  try {
    const result = await d.client.mutate({
      mutation: SET_ORDER_FLAGS,
      variables: { orderIds, flagged, changedAt },
    });
    const states: OrderFlagStateEntry[] = (
      result?.data?.setOrderFlags ?? []
    ).map((s: OrderFlagStateEntry) => ({
      ...s,
      changedAt: new Date(s.changedAt).toISOString(),
    }));
    d.store.dispatch(orderFlagChangeSettled(states, d.now()));
  } catch (err) {
    // Encolada sin red: es un éxito diferido, el pendiente se queda.
    if (isOfflineQueuedError(err)) return;
    d.store.dispatch(orderFlagChangeFailed(orderIds, changedAt));
    try {
      const i18n = (await import('../../i18n')).default;
      d.notifyError(i18n.t('common:tables.flag.saveError'));
    } catch {
      // El aviso es best-effort.
    }
  }
}
